import React, { useState, useEffect } from 'react';
// import { base44 } from '@/api/base44Client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { ArrowLeft, Loader2, ShieldAlert, FileText, Plus, AlertTriangle, Users, CheckCircle } from "lucide-react";
import { Link } from "react-router-dom";
// import { createPageUrl } from '@/utils';
import { Toaster, toast } from "sonner";
import { format } from "date-fns";
import { useAuth } from '../context/AuthContext';
// import TermsModal from '@/components/dashboard/TermsModal';
// import * as userApi from '@/components/api/userApi';
// import * as adminApi from '@/components/api/adminApi';

// -- Mock API placeholders until actual endpoints are available --
const adminApi = {
    getAllTermsVersions: async () => [
        {
            terms_version_id: 'v2',
            version: '2.0',
            content_text: 'Updated terms and conditions for 2024...',
            created_date: new Date().toISOString(),
            acceptance_count: 145
        },
        {
            terms_version_id: 'v1',
            version: '1.0',
            content_text: 'Initial terms and conditions...',
            created_date: new Date(Date.now() - 86400000 * 30).toISOString(),
            acceptance_count: 300
        }
    ],
    getTermsAcceptanceStats: async () => ({
        latest_version: '2.0',
        total_users: 200,
        total_acceptances: 145,
        acceptance_rate: 72.5
    }),
    createTermsVersion: async (content) => {
        console.log('Creating new terms version:', content);
        return { success: true, version: '2.1' };
    }
};

const userApi = {
    getTermsStatus: async (id) => ({ accepted: true, terms_version_id: 'v2', terms_content: "Terms here..." }),
    acceptTerms: async (id, version) => true
};
// ------------------------------------------------

export default function AdminTerms() {
    const { user, loading: authLoading } = useAuth();
    const [userLoading, setUserLoading] = useState(true);
    const [termsAccepting, setTermsAccepting] = useState(false);
    const [termsStatus, setTermsStatus] = useState(null);
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [newTermsContent, setNewTermsContent] = useState('');
    const queryClient = useQueryClient();

    useEffect(() => {
        // Simulate user loading
        if (!authLoading && user) {
            setUserLoading(false);
            setTermsStatus({ accepted: true });
        } else if (!authLoading && !user) {
            setUserLoading(false);
        }
    }, [user, authLoading]);

    const hasAcceptedTerms = termsStatus?.accepted === true;
    const isAdmin = user?.role === 'admin';

    const { data: termsVersions = [], isLoading } = useQuery({
        queryKey: ['adminTermsVersions'],
        queryFn: () => adminApi.getAllTermsVersions(),
        enabled: !!(hasAcceptedTerms && isAdmin),
    });

    const { data: termsStats } = useQuery({
        queryKey: ['adminTermsStats'],
        queryFn: () => adminApi.getTermsAcceptanceStats(),
        enabled: !!(hasAcceptedTerms && isAdmin),
    });

    const createTerms = useMutation({
        mutationFn: (content) => adminApi.createTermsVersion(content),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['adminTermsVersions'] });
            queryClient.invalidateQueries({ queryKey: ['adminTermsStats'] });
            setCreateDialogOpen(false);
            setNewTermsContent('');
            //   toast.success('New Terms & Conditions published. All users must re-accept.');
        },
        onError: (error) => {
            //   toast.error(error.message || 'Failed to create new terms');
        }
    });

    const acceptTerms = async () => {
        setTermsAccepting(true);
        // await userApi.acceptTerms(user.id, termsStatus.terms_version_id);
        setTermsStatus({ ...termsStatus, accepted: true });
        setTermsAccepting(false);
    };

    const handleCreateTerms = () => {
        if (!newTermsContent.trim()) {
            toast.error('Terms content is required');
            return;
        }
        createTerms.mutate(newTermsContent);
    };

    if (authLoading || userLoading) {
        return (
            <div className="h-screen bg-slate-900 flex items-center justify-center">
                <Loader2 className="w-10 h-10 text-blue-500 animate-spin" />
            </div>
        );
    }

    // Terms Modal Placeholder
    if (!hasAcceptedTerms) {
        return (
            <div className="h-screen bg-slate-900 flex items-center justify-center text-white">
                <div className="text-center">
                    <h2 className="text-xl mb-4">Terms Acceptance Required</h2>
                    <Button onClick={acceptTerms} disabled={termsAccepting}>
                        {termsAccepting ? "Accepting..." : "Accept Mock Terms"}
                    </Button>
                </div>
            </div>
        )
    }

    if (!isAdmin) {
        return (
            <div className="h-screen bg-slate-900 flex items-center justify-center">
                <div className="text-center">
                    <ShieldAlert className="w-16 h-16 text-red-500 mx-auto mb-4" />
                    <h1 className="text-xl font-bold text-white mb-2">Access Denied</h1>
                    <p className="text-slate-400 mb-4">Only administrators can access this page.</p>
                    <Link to="/dashboard">
                        <Button className="bg-blue-600 hover:bg-blue-700">Go to Dashboard</Button>
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-900 p-6">
            <Toaster richColors position="top-right" />

            <div className="max-w-5xl mx-auto">
                <div className="flex items-center gap-4 mb-8">

                    <div>
                        <h1 className="text-2xl font-bold text-white">Terms & Conditions</h1>
                        <p className="text-slate-400 text-sm mt-1">Manage T&C versions and view acceptance stats</p>
                    </div>
                </div>

                {/* Stats Cards */}
                {termsStats && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                        <Card className="bg-slate-950 border-slate-800">
                            <CardContent className="p-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                                        <FileText className="h-5 w-5 text-blue-400" />
                                    </div>
                                    <div>
                                        <p className="text-slate-400 text-sm">Current Version</p>
                                        <p className="text-white font-semibold">{termsStats.latest_version || 'None'}</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                        <Card className="bg-slate-950 border-slate-800">
                            <CardContent className="p-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                                        <CheckCircle className="h-5 w-5 text-emerald-400" />
                                    </div>
                                    <div>
                                        <p className="text-slate-400 text-sm">Accepted</p>
                                        <p className="text-white font-semibold">{termsStats.total_acceptances} / {termsStats.total_users}</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                        <Card className="bg-slate-950 border-slate-800">
                            <CardContent className="p-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                                        <Users className="h-5 w-5 text-purple-400" />
                                    </div>
                                    <div>
                                        <p className="text-slate-400 text-sm">Acceptance Rate</p>
                                        <p className="text-white font-semibold">{termsStats.acceptance_rate}%</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                )}

                {/* Create New Version Button */}
                <div className="mb-6">
                    <Button
                        onClick={() => setCreateDialogOpen(true)}
                        className="bg-blue-600 hover:bg-blue-700"
                    >
                        <Plus className="h-4 w-4 mr-2" />
                        Create New Version
                    </Button>
                </div>

                {/* Terms Versions List */}
                <div className="space-y-4">
                    {isLoading ? (
                        <Card className="bg-slate-950 border-slate-800">
                            <CardContent className="p-8 text-center">
                                <Loader2 className="h-6 w-6 text-blue-500 animate-spin mx-auto" />
                            </CardContent>
                        </Card>
                    ) : termsVersions.length === 0 ? (
                        <Card className="bg-slate-950 border-slate-800">
                            <CardContent className="p-8 text-center">
                                <FileText className="w-10 h-10 mx-auto mb-2 opacity-50 text-slate-500" />
                                <p className="text-slate-500">No Terms & Conditions created yet</p>
                            </CardContent>
                        </Card>
                    ) : (
                        termsVersions.map((terms, index) => (
                            <Card key={terms.terms_version_id} className="bg-slate-950 border-slate-800">
                                <CardHeader className="border-b border-slate-800">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <FileText className="h-5 w-5 text-blue-400" />
                                            <div>
                                                <CardTitle className="text-white">{terms.version}</CardTitle>
                                                <CardDescription className="text-slate-500">
                                                    Created {format(new Date(terms.created_date), 'MMM d, yyyy HH:mm')}
                                                </CardDescription>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            {index === 0 && (
                                                <Badge className="bg-emerald-500/20 text-emerald-400">Current</Badge>
                                            )}
                                            <Badge className="bg-slate-700 text-slate-300">
                                                {terms.acceptance_count} accepted
                                            </Badge>
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardContent className="p-4">
                                    <pre className="text-slate-400 text-sm whitespace-pre-wrap line-clamp-6 bg-slate-800/50 p-4 rounded-lg">
                                        {terms.content_text}
                                    </pre>
                                </CardContent>
                            </Card>
                        ))
                    )}
                </div>
            </div>

            {/* Create Terms Dialog */}
            <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
                <DialogContent className="bg-slate-900 border-slate-800 max-w-2xl">
                    <DialogHeader>
                        <DialogTitle className="text-white">Create New Terms & Conditions</DialogTitle>
                        <DialogDescription className="text-slate-400">
                            Publishing new terms will invalidate all previous acceptances. All users must re-accept.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
                        <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
                        <p className="text-amber-200 text-sm">
                            <strong>Warning:</strong> Creating a new version will force ALL users to re-accept the terms
                            before they can access the dashboard or receive notifications.
                        </p>
                    </div>

                    <Textarea
                        value={newTermsContent}
                        onChange={(e) => setNewTermsContent(e.target.value)}
                        placeholder="Enter the full Terms & Conditions text..."
                        className="bg-slate-800 border-slate-700 text-white h-64"
                    />

                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setCreateDialogOpen(false)}
                            className="border-slate-700 text-slate-300"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleCreateTerms}
                            disabled={createTerms.isPending}
                            className="bg-blue-600 hover:bg-blue-700"
                        >
                            {createTerms.isPending ? (
                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                            ) : null}
                            Publish New Version
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
